import { Injectable, inject } from '@angular/core';
import {
  doc, collection, serverTimestamp, setDoc, getDoc,
  query, startAfter, limit, getDocs,
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

import {
  OrganizationMember, OrganizationMembersPage, MemberDetails,
} from '../models/member.model';
import { Role, DEFAULT_MEMBER_PERMISSIONS } from '../models/role.model';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationMemberService {
  private readonly firebase = inject(FirebaseService);
  private readonly memberCache = new Map<string, OrganizationMember>();
  private readonly memberRoleCache = new Map<string, Role | null>();

  async getCurrentMember(organizationId: string): Promise<OrganizationMember> {
    if (!this.firebase.auth.currentUser) {
      throw new Error('User not logged in');
    }
    const userId = this.firebase.auth.currentUser.uid;
    const cacheKey = `crewdle_member_${organizationId}_${userId}`;

    if (this.memberCache.has(cacheKey)) return this.memberCache.get(cacheKey)!;

    const stored = sessionStorage.getItem(cacheKey);
    if (stored) {
      const member = JSON.parse(stored) as OrganizationMember;
      this.memberCache.set(cacheKey, member);
      return member;
    }

    const member = await this.getOrganizationMember(organizationId, userId);
    this.memberCache.set(cacheKey, member);
    sessionStorage.setItem(cacheKey, JSON.stringify(member));
    return member;
  }

  /** Bust member + role caches after a role/permission change. */
  invalidateMemberCache(organizationId: string, userId: string): void {
    const memberKey = `crewdle_member_${organizationId}_${userId}`;
    const roleKey = `crewdle_member_role_${organizationId}_${userId}`;
    this.memberCache.delete(memberKey);
    this.memberRoleCache.delete(roleKey);
    sessionStorage.removeItem(memberKey);
    sessionStorage.removeItem(roleKey);
  }

  async getOrganizationMembers(
    organizationId: string,
    limitPerPage: number,
    lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
  ): Promise<OrganizationMembersPage> {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    const membersRef = collection(organizationRef, 'members');

    let memberQuery = query(membersRef, limit(limitPerPage));
    if (lastDoc) {
      memberQuery = query(memberQuery, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(memberQuery);
    const members = querySnapshot.docs.map((d) => this.mapMemberData(d.data()));

    if (querySnapshot.size < limitPerPage) {
      return { members, lastVisible: null };
    }

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    return { members, lastVisible };
  }

  /** @param functions callables of the org's region when it isn't the active org's (staff cross-org view). */
  async listMembersDetailsOfOrganization(organizationId: string, functions?: Functions): Promise<MemberDetails> {
    const callable = httpsCallable<
      { organizationId: string },
      { users: MemberDetails }
    >(functions ?? this.firebase.functions, 'listMembersDetailsOfOrganization');

    const result = await callable({ organizationId });
    return result.data.users;
  }

  async getOrganizationMember(organizationId: string, memberId: string): Promise<OrganizationMember> {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    const memberRef = doc(organizationRef, 'members', memberId);
    const memberSnapshot = await getDoc(memberRef);

    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    return this.mapMemberData(memberSnapshot.data());
  }

  async addMemberToOrganization(organizationId: string, role: string, roleId?: string, userId?: string) {
    if (!this.firebase.auth.currentUser) {
      throw new Error('User not logged in');
    }
    const uid = userId || this.firebase.auth.currentUser.uid;

    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    const memberRef = doc(organizationRef, 'members', uid);
    await setDoc(memberRef, {
      userId: uid,
      role,
      ...(roleId && { roleId }),
      joinedAt: serverTimestamp(),
    });
  }

  async updateMemberRole(organizationId: string, memberId: string, role: string, roleId?: string) {
    const db = this.firebase.firestore;
    const memberRef = doc(db, 'organizations', organizationId, 'members', memberId);
    const update: Record<string, any> = { role };
    if (roleId) {
      update['roleId'] = roleId;
    }
    await setDoc(memberRef, update, { merge: true });
    this.invalidateMemberCache(organizationId, memberId);
  }

  async removeMemberFromOrganization(organizationId: string, memberId: string): Promise<boolean> {
    if (memberId === this.firebase.auth.currentUser?.uid) {
      throw new Error('Cannot remove yourself from the organization');
    }
    return this.removeMemberFromOrganizationFunction(organizationId, memberId);
  }

  async isCurrentUserAdmin(organizationId: string): Promise<boolean> {
    if (!this.firebase.auth.currentUser) return false;
    const member = await this.getOrganizationMember(organizationId, this.firebase.auth.currentUser.uid);
    return member.role === 'admin';
  }

  async getCurrentMemberRole(organizationId: string): Promise<Role | null> {
    if (!this.firebase.auth.currentUser) return null;
    const userId = this.firebase.auth.currentUser.uid;
    const cacheKey = `crewdle_member_role_${organizationId}_${userId}`;

    if (this.memberRoleCache.has(cacheKey)) return this.memberRoleCache.get(cacheKey)!;

    const stored = sessionStorage.getItem(cacheKey);
    if (stored) {
      const role = JSON.parse(stored) as Role | null;
      this.memberRoleCache.set(cacheKey, role);
      return role;
    }

    const member = await this.getCurrentMember(organizationId);
    const roleId = member.roleId || member.role;
    if (!roleId) {
      this.memberRoleCache.set(cacheKey, null);
      sessionStorage.setItem(cacheKey, JSON.stringify(null));
      return null;
    }

    const roleDoc = await getDoc(
      doc(this.firebase.firestore, 'organizations', organizationId, 'roles', roleId),
    );
    const role = roleDoc.exists()
      ? {
          id: roleDoc.id,
          name: roleDoc.data()['name'],
          description: roleDoc.data()['description'] || '',
          permissions: roleDoc.data()['permissions'] || [],
          isSystem: roleDoc.data()['isSystem'] ?? true,
          createdAt: roleDoc.data()['createdAt'],
          updatedAt: roleDoc.data()['updatedAt'],
          createdBy: roleDoc.data()['createdBy'] || '',
        }
      : this.buildFallbackRole(roleId, member.role);

    this.memberRoleCache.set(cacheKey, role);
    sessionStorage.setItem(cacheKey, JSON.stringify(role));
    return role;
  }

  private buildFallbackRole(roleId: string, memberRole: string): Role {
    const isAdmin = roleId === 'admin' || memberRole === 'admin';
    return {
      id: roleId,
      name: roleId.charAt(0).toUpperCase() + roleId.slice(1),
      description: '',
      permissions: isAdmin ? ['*'] : DEFAULT_MEMBER_PERMISSIONS,
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    };
  }

  async hasPermission(organizationId: string, permission: string): Promise<boolean> {
    const role = await this.getCurrentMemberRole(organizationId);
    if (!role) return false;
    return role.permissions.includes(permission) || role.permissions.includes('*');
  }

  private mapMemberData(memberData?: DocumentData): OrganizationMember {
    if (!memberData) throw new Error('Member data is required');
    return {
      userId: memberData['userId'],
      role: memberData['role'],
      roleId: memberData['roleId'],
      permissions: memberData['permissions'],
      joinedAt: memberData['joinedAt'],
      syncedFromMicrosoft: memberData['syncedFromMicrosoft'],
      microsoftUserId: memberData['microsoftUserId'],
      lastSyncedAt: memberData['lastSyncedAt'],
    };
  }

  private async removeMemberFromOrganizationFunction(organizationId: string, userId: string): Promise<boolean> {
    const callable = httpsCallable<
      { organizationId: string; userId: string },
      { success: boolean }
    >(this.firebase.functions, 'removeUserFromOrganization');
    const result = await callable({ organizationId, userId });
    return result.data.success;
  }
}
